import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Search } from 'lucide-react';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';

interface SpeciesHit {
    id: number;
    scientific_name: string;
    common_names_es: string | null;
}

function primaryCommonName(value: string | null | undefined): string {
    const first = value?.split(',')[0]?.trim();
    return first || '';
}

interface CaptureSpeciesEditorProps {
    currentSpecies: string | null;
    onSave: (scientificName: string) => Promise<void>;
}

export function CaptureSpeciesEditor({ currentSpecies, onSave }: CaptureSpeciesEditorProps) {
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<SpeciesHit | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const query = search.trim();
    const { data: results = [], isLoading, isError } = useQuery({
        queryKey: ['admin', 'species-catalog-search', query],
        queryFn: async () => {
            const { data, error: rpcError } = await dbClient.rpc('get_species_catalog', {
                p_search_query: query,
                p_limit: 12,
            });
            if (rpcError) throw rpcError;
            return normalizeRpcResponse(data) as SpeciesHit[];
        },
        enabled: query.length >= 2 && !selected,
        staleTime: 30_000,
    });

    const sameSpecies = selected
        && currentSpecies
        && selected.scientific_name.trim().toLowerCase() === currentSpecies.trim().toLowerCase();

    const handleSave = async () => {
        if (!selected || sameSpecies || saving) return;
        setSaving(true);
        setError(null);
        try {
            await onSave(selected.scientific_name);
            setSelected(null);
            setSearch('');
        } catch (e: unknown) {
            const message = e && typeof e === 'object' && 'message' in e
                ? String((e as { message?: string }).message)
                : 'No se ha podido cambiar la especie';
            setError(message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mt-4 rounded-lg border border-border bg-muted/20 p-3 space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Especie</p>
            {selected ? (
                <div className="space-y-2">
                    <p className="text-sm text-foreground">
                        Quedará como{' '}
                        <strong>{primaryCommonName(selected.common_names_es) || selected.scientific_name}</strong>
                        {primaryCommonName(selected.common_names_es) ? ` (${selected.scientific_name})` : ''}.
                        El álbum de este usuario se actualiza.
                    </p>
                    {error && <p className="text-xs text-destructive">{error}</p>}
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving || !!sameSpecies}
                            className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
                        >
                            {saving ? 'Guardando…' : sameSpecies ? 'Ya es esta especie' : 'Guardar especie'}
                        </button>
                        <button
                            type="button"
                            onClick={() => { setSelected(null); setError(null); }}
                            disabled={saving}
                            className="px-3 py-1.5 rounded-lg border border-border bg-background text-sm font-medium disabled:opacity-50"
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            ) : (
                <div>
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Busca por nombre común o científico"
                            className="w-full py-2 pr-3 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary/50"
                            style={{ paddingLeft: '2rem' }}
                            aria-label="Buscar especie del catálogo"
                        />
                    </div>
                    {query.length >= 2 && (
                        <div className="mt-1 rounded-lg border border-border bg-card max-h-48 overflow-y-auto">
                            {isLoading ? (
                                <div className="p-3 flex items-center gap-2 text-sm text-muted-foreground">
                                    <Loader2 size={14} className="animate-spin" />
                                    Buscando…
                                </div>
                            ) : isError ? (
                                <p className="p-3 text-sm text-destructive">No se ha podido buscar en el catálogo</p>
                            ) : results.length === 0 ? (
                                <p className="p-3 text-sm text-muted-foreground">Ninguna especie del catálogo coincide</p>
                            ) : (
                                results.map((species) => {
                                    const common = primaryCommonName(species.common_names_es);
                                    return (
                                        <button
                                            key={species.id}
                                            type="button"
                                            onClick={() => setSelected(species)}
                                            className="w-full px-3 py-2 text-left hover:bg-muted/50 border-b border-border last:border-0"
                                        >
                                            <span className="block text-sm font-medium text-foreground">
                                                {common || species.scientific_name}
                                            </span>
                                            {common && (
                                                <span className="block text-xs text-muted-foreground">{species.scientific_name}</span>
                                            )}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
